/**
 * Markdown 图片尺寸：扩展语法与渲染
 *
 * 支持写法（可混用）：
 * 1. ![说明](url =400x300)  ![说明](url =400x)  ![说明](url =50%)
 * 2. ![说明|400x300](url)   ![说明|400](url)
 * 3. ![说明](url "400x300") 标题/引号内写尺寸
 */

/** @param {string} src */
export function preprocessImageSizeSyntax(src) {
  if (!src) return src;
  return src.replace(
    /!\[([^\]]*)\]\(\s*<?([^>\s)]+)>?\s*=\s*(\d+%?(?:x\d*%?)?)\s*\)/gi,
    '![$1]($2 "$3")'
  );
}

/**
 * @param {string} raw
 * @returns {{ width?: string, height?: string }|null}
 */
export function parseImageSize(raw) {
  if (!raw) return null;
  const text = String(raw).trim();
  let m = text.match(/^(\d+%?)(?:x(\d+%?))?$/i);
  if (m) {
    const size = {width: m[1]};
    if (m[2]) size.height = m[2];
    return size;
  }
  m = text.match(/^width[=:\s]*(\d+%?)(?:[,;\s]+height[=:\s]*(\d+%?))?$/i);
  if (m) {
    const size = {width: m[1]};
    if (m[2]) size.height = m[2];
    return size;
  }
  return null;
}

/**
 * @param {string} alt
 * @returns {{ alt: string, size: object|null }}
 */
export function splitAltImageSize(alt) {
  const text = alt || '';
  const m = text.match(/^(.+?)\|(\d+%?(?:x\d+%?)?)\s*$/);
  if (!m) return {alt: text, size: null};
  return {
    alt: m[1].trim(),
    size: parseImageSize(m[2]),
  };
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function buildSizeAttrs(size) {
  if (!size) return '';
  const styles = ['max-width:100%'];
  if (size.width) styles.push(`width:${size.width}`);
  if (size.height) styles.push(`height:${size.height}`);
  else if (size.width) styles.push('height:auto');
  return `style="${escapeHtml(styles.join(';'))}"`;
}

export default function markdownItImageSize(md) {
  md.core.ruler.before('inline', 'doc_image_size_preprocess', (state) => {
    state.src = preprocessImageSizeSyntax(state.src);
  });

  const defaultRender =
    md.renderer.rules.image ||
    function(tokens, idx, options, env, self) {
      return self.renderToken(tokens, idx, options);
    };

  md.renderer.rules.image = function(tokens, idx, options, env, self) {
    const token = tokens[idx];
    const src = token.attrGet('src') || '';
    const title = token.attrGet('title') || '';
    const split = splitAltImageSize(token.content || '');
    let alt = split.alt;
    let size = split.size || parseImageSize(title);

    const srcMatch = src.match(/^(.+?)\s+=\s*(\d+%?(?:x\d*%?)?)$/i);
    let href = src;
    if (srcMatch) {
      href = srcMatch[1];
      size = size || parseImageSize(srcMatch[2]);
    }

    const sizeAttrs = buildSizeAttrs(size);
    const altAttr = alt ? ` alt="${escapeHtml(alt)}"` : '';
    const titleAttr = title && !size ? ` title="${escapeHtml(title)}"` : '';
    if (sizeAttrs) {
      return `<img src="${escapeHtml(href)}"${altAttr}${titleAttr} ${sizeAttrs} />`;
    }
    return defaultRender(tokens, idx, options, env, self);
  };
}
